import {
  Box,
  Button,
  Center,
  Flex,
  Spacer,
  Stack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { compact, get, isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';

import { useForm, useWatch } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { type TypedDocumentNode, useClient, useMutation, useQuery } from 'urql';

import { isRoleRevelioAdmin, useGetLoggedInUser } from '@revelio/auth';
import { Loading } from '@revelio/core';
import {
  CustomTaxonomyEnum,
  type GetClientDetailsQuery,
  type KeyValueResp,
  MetadataKey,
  Role,
} from '@revelio/data-access';

import { showAlertDialog } from '../../../shared/components/alert-dialog';
import ClientForm, {
  type ClientFormValues,
  type ReportSelectOption,
  type TabSelectOption,
} from '../client-form';
import { DataBuilderEditor } from '../data-builder-settings/data-builder-editor';
import {
  parseRequestsPerSecond,
  serializeRequestsPerSecond,
} from '../requests-per-second-modal';
import { UsageAnalyticsModal } from '../usage-analytics/UsageAnalyticsModal';
import type { UserFormValues } from '../user-form';
import {
  ClientDetailsQuery,
  UpdateClientGroupMutation,
} from '../userOperations';
import {
  getMetadataValue,
  getReportOptions,
  mapServerReportResponseToSelectOptions,
  serializeOptions,
  tabOptions,
  useAllClientUsersQuery,
} from '../utils/helpers';

const PROTECTED_CLIENT_GROUPS = ['revelio_self_signup', 'Revelio Labs'];
const apiMetadataKeys = [
  MetadataKey.RequestsPerSecond,
  MetadataKey.RequestsPerMonth,
];

const AdminClientEdit = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const client = useClient();
  const usageModal = useDisclosure();
  const clientId = searchParams.get('client');
  const isProtectedClientGroup =
    !!clientId && PROTECTED_CLIENT_GROUPS.includes(clientId);
  const [{ fetching: fetchingClient, data: clientData }] = useQuery({
    query: ClientDetailsQuery,
    variables: { ids: [clientId as string] },
    pause: !clientId,
  });

  const { result: clientUsersResult } = useAllClientUsersQuery(clientId ?? '');
  const activeUserCount = (
    clientUsersResult.data?.clients?.[0]?.users ?? []
  ).filter((u) => u?.active).length;

  const {
    loggedInUser,
    query: [{ fetching: loggedInUserLoading }],
  } = useGetLoggedInUser();

  const isRevelioAdmin = isRoleRevelioAdmin(loggedInUser.role);
  // as a client admin, the client settings are hidden and cancel button added next to update user button
  const isClientAdmin = loggedInUser.role === Role.ClientAdmin;
  const loggedInClientId = loggedInUser?.client_name;

  const [{ fetching: fetchingLoggedInClient, data: loggedInClientData }] =
    useQuery({
      query: ClientDetailsQuery,
      variables: { ids: [loggedInClientId as string] },
      pause: !loggedInClientId,
    });

  const clientToEdit = get(clientData, 'clients[0]', {});
  const [, updateClientGroup] = useMutation(UpdateClientGroupMutation);

  const [selectedClient, setSelectedClient] = useState(clientToEdit);
  const [reportOptions, setReportOptions] = useState<ReportSelectOption[]>([]);

  const { control, watch } = useForm<UserFormValues>();

  const {
    control: clientControl,
    register: clientFormRegister,
    formState: { isSubmitting: clientIsSubmitting, errors },
    handleSubmit: clientHandleSubmit,
    setValue: clientSetValue,
    reset,
    watch: clientWatch,
  } = useForm<ClientFormValues>();

  useEffect(() => {
    clientSetValue('num_seats', clientToEdit.num_seats);
  }, [clientToEdit.num_seats, clientSetValue]);

  useEffect(() => {
    setSelectedClient((prev: unknown) =>
      isEqual(prev, clientToEdit) ? prev : clientToEdit
    );
  }, [clientToEdit]);

  const hasReportsTab = clientWatch('tabs', [])?.some(
    (tab) => tab?.value === 'reports'
  );

  const submitEditClient = clientHandleSubmit((formData) => {
    if (isProtectedClientGroup) return;
    const reportTypesToUse = formData.report_types
      ? serializeOptions(formData.report_types).reportTypes
      : selectedClient.reports_configuration?.report_types;

    const apiMetadata = [
      {
        key: MetadataKey.RequestsPerSecond,
        value: serializeRequestsPerSecond(formData.requests_per_second),
      },
      {
        key: MetadataKey.RequestsPerMonth,
        value: formData.requests_per_month,
      },
    ].filter(
      (metadataValue) =>
        metadataValue.value !== undefined &&
        metadataValue.value !== null &&
        metadataValue.value !== ''
    );
    const existingNonApiMetadata = (
      (selectedClient.metadata ?? []) as Array<KeyValueResp | null>
    )
      .filter(
        (metadataValue) =>
          metadataValue?.key !== undefined &&
          metadataValue?.key !== null &&
          !apiMetadataKeys.includes(metadataValue.key)
      )
      .map((metadataValue) => ({
        key: metadataValue?.key,
        value: metadataValue?.value,
      }));
    const metadata = isRevelioAdmin
      ? [...existingNonApiMetadata, ...apiMetadata]
      : undefined;

    const doUpdate = () =>
      updateClientGroup({
        client_name: clientId as string,
        tabs: compact(formData.tabs).map((tab) => tab.value),
        live: formData.live,
        active: formData.active,
        linkup_postings: formData.linkup_postings,
        num_seats: formData?.num_seats?.toString(),
        ...(metadata ? { metadata } : {}),
        custom_taxonomy:
          formData.custom_taxonomy?.value ||
          formData.custom_taxonomy ||
          CustomTaxonomyEnum.Undefined,
        ...(hasReportsTab && reportTypesToUse
          ? { reports_configuration: { report_types: reportTypesToUse } }
          : {}),
      }).then((result) => {
        if (result.error) {
          return;
        }

        toast({
          title: 'client group updated',
          description: 'The client group has now been updated',
          status: 'success',
          duration: 4000,
          position: 'top-right',
        });
      });

    if (formData.active === false && selectedClient.active === true) {
      showAlertDialog({
        alertType: 'confirm',
        isOpen: true,
        headerText: 'Deactivate this client group?',
        bodyContent: (
          <span>
            This will immediately revoke dashboard access for all{' '}
            <strong>{activeUserCount}</strong> active user
            {activeUserCount !== 1 ? 's' : ''} in this group. They will be
            logged out on their next request.
          </span>
        ),
        footerAcceptIsDestructive: true,
        footerAcceptText: 'Deactivate',
        onAccept: doUpdate,
      });
      return;
    }

    return doUpdate();
  });

  const selectedClientGroup = useWatch({
    control,
    name: 'client_group.value',
  }); // initially watch returns undefined when set by the defaultValue of clientId
  const isUserClientGroupEdited =
    (selectedClientGroup || clientId) !== clientId;
  const watchedClientGroup = watch('client_group');
  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    if (clientId && !fetchingLoggedInClient && loggedInClientData) {
      const fetchClientDetails = async () => {
        try {
          const query: TypedDocumentNode<
            GetClientDetailsQuery,
            { ids: string[] }
          > = ClientDetailsQuery;
          const { data } = await client
            .query(query, {
              ids: [clientId],
            })
            .toPromise();

          const clientData = get(data, 'clients[0]', {});

          const loggedInClient = get(loggedInClientData, 'clients[0]', {});

          if (
            loggedInClient?.reports_configuration &&
            clientData?.client_name
          ) {
            setSelectedClient(clientData);

            const allReportTypes = getReportOptions(loggedInClient);

            setReportOptions(allReportTypes);

            reset({
              ...clientData,
              requests_per_second: parseRequestsPerSecond(
                getMetadataValue(
                  clientData.metadata,
                  MetadataKey.RequestsPerSecond
                )
              ),
              requests_per_month:
                getMetadataValue(
                  clientData.metadata,
                  MetadataKey.RequestsPerMonth
                ) ?? '',
              tabs: [
                ...(clientData.tabs.map((tab: string) =>
                  tabOptions.find((o) => o.value === tab)
                ) as TabSelectOption[]),
              ],
              report_types: mapServerReportResponseToSelectOptions(
                clientData.reports_configuration?.report_types,
                allReportTypes
              ),
            });
          }
          // biome-ignore lint/correctness/noUnusedVariables: migrated from develop
        } catch (error) {
          // Handle error if needed
        }
      };

      fetchClientDetails();
    }
  }, [
    watchedClientGroup,
    client,
    clientToEdit,
    clientSetValue,
    reset,
    clientId,
    fetchingLoggedInClient,
    loggedInClientData,
  ]);

  return (
    <Box h="100%">
      {!fetchingClient && !loggedInUserLoading ? (
        <form style={{ height: '100%' }} onSubmit={submitEditClient}>
          <ClientForm
            errors={errors}
            clientToEdit={selectedClient}
            title={`Edit client group: ${selectedClient.client_name}`}
            description={
              isProtectedClientGroup
                ? 'This client group is read-only. Please contact engineering to make changes.'
                : 'Affects all users in group'
            }
            readonly={
              isUserClientGroupEdited || isProtectedClientGroup || isClientAdmin
            }
            register={clientFormRegister}
            control={clientControl}
            watch={clientWatch}
            reportOptions={reportOptions}
            renderDataBuilderConfig={() => (
              <DataBuilderEditor.EditFlow
                clientName={clientId}
                readonly={
                  isUserClientGroupEdited ||
                  isProtectedClientGroup ||
                  isClientAdmin
                }
              />
            )}
            actions={
              <Stack direction="row" spacing={2} align="center">
                <Button
                  colorScheme="navyBlue"
                  variant="solid"
                  size="md"
                  onClick={() => {
                    const newSearchParams = new URLSearchParams(searchParams);

                    newSearchParams.delete('editing');
                    newSearchParams.delete('client');

                    setSearchParams(newSearchParams);
                  }}
                >
                  Cancel
                </Button>
                {isRevelioAdmin && !isProtectedClientGroup && (
                  <Button
                    colorScheme="green"
                    variant="solid"
                    size="md"
                    isLoading={clientIsSubmitting}
                    type="submit"
                    isDisabled={isUserClientGroupEdited}
                  >
                    Update Client
                  </Button>
                )}
                <Spacer />
                {isRevelioAdmin && (
                  <Button
                    colorScheme="navyBlue"
                    variant="outline"
                    size="md"
                    onClick={usageModal.onOpen}
                  >
                    Download usage analytics
                  </Button>
                )}
                {isRevelioAdmin && (
                  <Button
                    colorScheme="green"
                    variant="solid"
                    size="md"
                    onClick={() => {
                      const newSearchParams = new URLSearchParams(searchParams);

                      newSearchParams.delete('editing');
                      newSearchParams.set('client', clientId ?? '');

                      setSearchParams(newSearchParams);
                    }}
                  >
                    Show Users
                  </Button>
                )}
              </Stack>
            }
          />
        </form>
      ) : (
        <Flex justify="center" minHeight="200px">
          <Center>
            <Loading />
          </Center>
        </Flex>
      )}
      <UsageAnalyticsModal
        clientName={clientId ?? ''}
        isOpen={usageModal.isOpen}
        onClose={usageModal.onClose}
      />
    </Box>
  );
};

export default AdminClientEdit;
